import { useCallback, useState } from 'react';
import Link from 'next/link';
import { dataLayerPush } from '@ertelecom/ui-react/components/helpers';
import { IMenuItem } from '~/src/api/content/menu';
import { PAYMENTS_ITEM_ID, SERVICE_ITEM_ID, PROMO_ITEM_ID } from '~/src/components/Header';
import Accordeon from '~/src/components/Header/components/Accordeon';
import Card from '~/src/components/Header/components/Card';
import Promo from '~/src/components/Header/components/Promo';
import * as ST from './styled';
import { EVENTS } from '~/src/constants/analytics';

interface IProps {
    menuItems: IMenuItem[];
    isTablet: boolean;
    isFixed?: boolean;
    onClick: (item: string) => void;
    onHover: (item: string) => void;
    clickHandler?: () => void;
}

const MainMenu = ({ menuItems, isTablet, isFixed = false, onClick, onHover, clickHandler }: IProps): JSX.Element => {
    const [menuItemHeight, setMenuItemHeight] = useState(0);

    const isTarriffItem = useCallback((id: number) => {
        if (id !== PAYMENTS_ITEM_ID && id !== SERVICE_ITEM_ID && id !== PROMO_ITEM_ID) {
            return true;
        }
    }, []);

    const isAccordeonItem = useCallback((id: number) => id === PAYMENTS_ITEM_ID || id === PROMO_ITEM_ID, []);

    const buttonClick = useCallback((item) => {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: 'Main menu click',
            action: `Кнопка Посмотреть тарифы раздел ${item}`,
            label: 'menu_new',
        });
    }, []);

    const isDuplicateMenu = useCallback((title: string) => {
        return [
            'Об услуге',
            'Тарифы и подключение',
            'Тарифы и подключение для квартиры'
        ].includes(title);
    }, []);

    const getMenuItemHeight = useCallback((id) => {
        const itemHeight = document.getElementById(id)?.clientHeight || 0;
        setMenuItemHeight(itemHeight);
    }, []);

    return (
        <ST.MainMenuWrapper
            isTablet={isTablet}
            isFixed={isFixed}
            menuItemHeight={menuItemHeight}
            onMouseLeave={() => setMenuItemHeight(0)}
        >
            {menuItems.map(item => (
                (!isTablet
                    ? <ST.MenuItem
                        key={item.id}
                        isPromoted={!!item.promoted}
                        withButton={isTarriffItem(item.id)}
                        onMouseEnter={() => getMenuItemHeight(item.id)}
                    >
                        <Link
                            href={item.link}
                            prefetch={false}
                        >
                            <a
                                onClick={() => onClick(item.title)}
                                onMouseEnter={() => onHover(item.title)}
                            >
                                {item.title}
                            </a>
                        </Link>
                        <ST.CollapsedMenu isFixed={isFixed} id={item.id.toString()}>
                            <ST.CollapsedWrapper>
                                <ST.Row between='xs'>
                                    <ST.Col xs={4} md={3}>
                                        {item.desc && (
                                            <ST.MenuTitle
                                                dangerouslySetInnerHTML={{ __html: item.desc }}
                                            />
                                        )}
                                        {item.add_desc && (
                                            <ST.MenuDescription
                                                dangerouslySetInnerHTML={{ __html: item.add_desc }}
                                            />
                                        )}
                                        <Link href={item.button_link || item.link} prefetch={false}>
                                            <ST.Button
                                                href={item.button_link || item.link}
                                                onClick={() => buttonClick(item.title)}
                                            >
                                                {item.button_text || 'Подключить'}
                                            </ST.Button>
                                        </Link>
                                    </ST.Col>
                                    <ST.Col xs={5}>
                                        <ST.MenuItemsRow>
                                            {item.children?.map(childItem => (
                                                <ST.Col xs={6} key={childItem.id}>
                                                    <Link href={childItem.link} passHref>
                                                        <ST.ChildMenuItem
                                                            onClick={() => onClick(
                                                                isDuplicateMenu(childItem.title)
                                                                    ? `${childItem.title} ${item.title}`
                                                                    : childItem.title
                                                            )}
                                                            onMouseEnter={() => onHover(
                                                                isDuplicateMenu(childItem.title)
                                                                    ? `${childItem.title} ${item.title}`
                                                                    : childItem.title
                                                                )}
                                                        >
                                                            {childItem.title}
                                                        </ST.ChildMenuItem>
                                                    </Link>
                                                </ST.Col>
                                            ))}
                                        </ST.MenuItemsRow>
                                    </ST.Col>
                                    <ST.Col xs={3}>
                                        {item.image && <Promo item={item} />}
                                    </ST.Col>
                                </ST.Row>
                            </ST.CollapsedWrapper>
                        </ST.CollapsedMenu>
                    </ST.MenuItem>
                    : isAccordeonItem(item.id)
                        ? <Accordeon
                            key={item.id}
                            item={item}
                            clickHandler={clickHandler}
                            onClick={onClick}
                        />
                        : <Card
                            key={item.id}
                            item={item}
                            isTarriff={isTarriffItem(item.id)}
                            onClick={onClick}
                            clickHandler={clickHandler}
                        />
                )
            ))}
        </ST.MainMenuWrapper>
    );
};

export default MainMenu;
